import { useState, useEffect, useCallback } from 'react';

export interface UseActiveSectionOptions {
  sectionIds: readonly string[];
  enabled?: boolean;
  offset?: number;
}

export interface UseActiveSectionReturn {
  activeId: string;
  activeIndex: number;
  scrollToSection: (target: string | number) => void;
}

// * [HOOK] High-performance viewport active section tracking
// ? Uses passive scroll tracking and RAF to synchronize active nav indicator smoothly without lag
export const useActiveSection = ({
  sectionIds,
  enabled = true,
  offset = 120,
}: UseActiveSectionOptions): UseActiveSectionReturn => {
  const [activeId, setActiveId] = useState<string>(sectionIds[0] || 'home');

  // * Synchronizes active nav link whenever section crosses central viewport threshold
  // ! Cleanup listener on unmount to avoid ghost listeners during route or hot-module updates
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    let ticking = false;

    const determineActiveSection = () => {
      const scrollPos = window.scrollY + offset;
      let currentId = sectionIds[0] || 'home';

      for (let i = 0; i < sectionIds.length; i++) {
        const id = sectionIds[i];
        const element = document.getElementById(id);
        if (element) {
          const top = element.offsetTop;
          if (scrollPos >= top) {
            currentId = id;
          }
        }
      }

      setActiveId(currentId);
      ticking = false;
    };

    // Determine initial state once elements are rendered
    determineActiveSection();

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(determineActiveSection);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [sectionIds, enabled, offset]);

  // * Native smooth scroll navigation helper
  // ? Handles both string IDs ('skills') and numerical indices (2)
  const scrollToSection = useCallback(
    (target: string | number): void => {
      const targetId =
        typeof target === 'number' ? sectionIds[target] : target;
      if (!targetId) return;

      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveId(targetId);
      }
    },
    [sectionIds]
  );

  const activeIndex = Math.max(0, sectionIds.indexOf(activeId));

  return { activeId, activeIndex, scrollToSection };
};

export default useActiveSection;

